// src/data/immigrationGuides/lifeInUKRequirement.js

const lifeInUKRequirement = {
    slug: "life-in-uk-requirement",

    title: "Life in the UK Test Requirement for ILR and Citizenship",

    seo: {
        metaTitle:
            "Life in the UK Test for ILR & Citizenship: Complete Guide",
        metaDescription:
            "Learn about the Life in the UK Test requirement, topics covered, format, preparation and how it is used for ILR and British citizenship applications.",
        keywords: [
            "Life in the UK Test",
            "ILR Life in UK Test",
            "citizenship test UK",
            "Life in UK preparation"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "Life in the UK Requirement",
            url: "/life-in-uk-requirement"
        }
    ],

    introduction: `
The [Life in the UK Test](https://www.gov.uk/life-in-the-uk-test) is a requirement for many people applying for
Indefinite Leave to Remain or British citizenship.

The test checks knowledge of life in the United Kingdom, including history,
government, laws and traditions.
`,

    sections: [
        {
            heading: "Who needs the Life in the UK Test?",
            content: `
Many applicants applying for:

- settlement (ILR)
- British citizenship

must pass the Life in the UK Test unless they qualify for an exemption.
`
        },

        {
            heading: "Test format",
            content: `
The official Life in the UK Test consists of:

- 24 questions
- 45 minutes

Questions are based on information from the official Life in the UK Handbook.
`
        },

        {
            heading: "Topics covered",
            content: `
The test includes topics such as:

- British history
- government and law
- rights and responsibilities
- traditions and customs
- UK society
`
        },

        {
            heading: "Preparing for the test",
            content: `
Effective preparation usually includes:

- studying the official handbook
- practising questions
- reviewing explanations
- revising weaker topics

Practice questions can help learners become familiar with the format.
`
        },

        {
            heading: "Booking the test",
            content: `
You must [book the Life in the UK Test online](https://www.gov.uk/life-in-the-uk-test/book-life-in-uk-test), at least 3 days in advance, through the only
official government booking service.

Key booking facts:

- The test currently costs £50 (always check GOV.UK for the current fee, as prices can change).
- There are over 30 test centres across the UK, and you choose which one to use when you book.
- To book, you need an email address, a debit or credit card, and an accepted form of ID.
- If you have an eVisa, you can use a share code to book instead of a physical document, see our
  eVisa guide for how to generate one.
- The name on your booking must exactly match the name on your ID.
- You do not need to take the test if you are under 18, are 65 or over, have already passed it
  before, or have a long-term physical or mental condition supported by an exemption form or
  doctor's letter.
`
        },

        {
            heading: "What happens at the test",
            content: `
On the day, here is what to expect:

- You have 45 minutes to answer 24 questions based on the official [Life in the UK handbook](https://www.gov.uk/life-in-the-uk-test/what-happens-test).
- You cannot bring children or other family members into the test centre.
- You must bring the same original ID you used to book (not a copy or screenshot), and your photo
  will be taken on the day to confirm your identity.
- You will not be able to take the test, and will not get a refund, if you bring the wrong ID or
  refuse to have your photo taken.
- If you pass, you'll receive a unique reference number, which you need for your citizenship or
  settlement application.
- If you fail, you can rebook as many times as you need, paying the fee each time.
- If you cancel or rearrange at least 3 days (72 hours) before your test, you get a refund.
  Cancelling or rearranging within 3 days does not qualify for a refund.
`
        }
    ],

    checklist: [
        "Read the official handbook",
        "Understand key topics",
        "Practise mock questions",
        "Book through official channels",
        "Keep test pass details safe"
    ],

    faqs: [
        {
            question: "Is Life in the UK Test required for ILR?",
            answer:
                "Many ILR applicants need to pass the Life in the UK Test unless exempt."
        },
        {
            question: "Is Life in the UK Test required for citizenship?",
            answer:
                "Most adult citizenship applicants need to pass the test unless exempt."
        },
        {
            question: "How many questions are in the Life in the UK Test?",
            answer:
                "The official test contains 24 questions, and you have 45 minutes to answer them."
        },
        {
            question: "How much does the Life in the UK Test cost?",
            answer:
                "The test currently costs £50, booked through the official GOV.UK service. Fees can change, so always check GOV.UK for the current price."
        },
        {
            question: "What do I need to book the Life in the UK Test?",
            answer:
                "You need an email address, a debit or credit card, and an accepted form of ID (or a share code if you have an eVisa). The name on your booking must exactly match the name on your ID."
        },
        {
            question: "What pass mark do I need for the Life in the UK Test?",
            answer:
                "You must score 75% or more (at least 18 out of 24 questions correct) to pass."
        },
        {
            question: "Can I get a refund if I cancel my Life in the UK Test?",
            answer:
                "Yes, if you cancel or rearrange at least 3 days (72 hours) before your test. Cancelling within 3 days of the test does not qualify for a refund."
        },
        {
            question: "What happens if I fail the Life in the UK Test?",
            answer:
                "You can rebook and retake the test as many times as you need, but you must pay the fee again each time."
        }
    ],

    relatedPages: [
        {
            title: "Practice Tests",
            url: "/mock-exams"
        },
        {
            title: "Government and Law Study Guide",
            url: "/study-guide/government-and-law"
        },
        {
            title: "British History Study Guide",
            url: "/study-guide/british-history"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Life in the UK Test",
            url: "https://www.gov.uk/life-in-the-uk-test"
        },
        {
            title: "GOV.UK - What Happens at the Test",
            url: "https://www.gov.uk/life-in-the-uk-test/what-happens-test"
        },
        {
            title: "GOV.UK - Book Life in the UK Test",
            url: "https://www.gov.uk/life-in-the-uk-test/book-life-in-uk-test"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-28"
    }
};

export default lifeInUKRequirement;